import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface SportsArticle {
  id: number;
  title: string;
  sport_type: string;
  league: string;
  source: string;
  content: string;
  link: string;
  views: number;
  likes: number;
  comments: number;
  created_at: string;
}

const sportLabelMap: Record<string, string> = {
  soccer: "축구",
  baseball: "야구",
  basketball: "농구",
  volleyball: "배구",
  hockey: "하키",
};

export function useSportsArticles(sportType?: string) {
  const [articles, setArticles] = useState<SportsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      let query = supabase
        .from("sports_articles")
        .select("id, title, sport_type, league, source, content, link, views, created_at");

      if (sportType) {
        query = query.eq("sport_type", sportType);
      }

      const { data, error: fetchError } = await query
        .order("created_at", { ascending: false })
        .limit(20);

      if (fetchError) {
        setError("스포츠 정보를 불러오는 중 오류가 발생했습니다");
        setArticles([]);
        return;
      }

      if (!data || data.length === 0) {
        setArticles([]);
        return;
      }

      const articleIds = data.map((a) => a.id);

      const { data: likeData } = await supabase
        .from("article_likes")
        .select("article_id")
        .in("article_id", articleIds);

      const likeMap: Record<number, number> = {};
      if (likeData) {
        likeData.forEach((l) => {
          likeMap[l.article_id] = (likeMap[l.article_id] || 0) + 1;
        });
      }

      const { data: commentData } = await supabase
        .from("article_comments")
        .select("article_id")
        .in("article_id", articleIds);

      const commentMap: Record<number, number> = {};
      if (commentData) {
        commentData.forEach((c) => {
          commentMap[c.article_id] = (commentMap[c.article_id] || 0) + 1;
        });
      }

      const mapped: SportsArticle[] = data.map((a) => ({
        id: a.id,
        title: a.title,
        sport_type: a.sport_type,
        league: a.league || "",
        source: a.source,
        content: a.content || "",
        link: a.link || "",
        views: a.views || 0,
        likes: likeMap[a.id] || 0,
        comments: commentMap[a.id] || 0,
        created_at: a.created_at,
      }));

      setArticles(mapped);
    } catch {
      setError("스포츠 정보를 불러오는 중 오류가 발생했습니다");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [sportType]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  return { articles, loading, error, refetch: fetchArticles, sportLabelMap };
}